import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { LockKeyhole, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function ResetPassword() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const { setUser } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) toast.error(t("auth.reset.tokenMissing"));
    }, [token, t]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 6) { toast.error(t("auth.signup.errors.passwordMin")); return; }
        if (password !== confirm) { toast.error(t("auth.reset.mismatch")); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/auth/reset-password", { token, password });
            localStorage.setItem("medx_token", data.token);
            setUser(data.user);
            toast.success(t("auth.reset.success"));
            navigate(data.user.role === "admin" ? "/admin" : data.user.role === "doctor" ? "/doctor" : "/dashboard");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white min-h-screen" data-testid="reset-password-page">
            <Navbar />
            <main className="pt-28 pb-20">
                <section className="max-w-md mx-auto px-6">
                    <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-3 text-center">PASSWORD RESET</div>
                    <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter text-center mb-8">
                        {t("auth.reset.title")}
                    </h1>

                    <div className="card-soft p-7">
                        {!token ? (
                            <div className="text-center space-y-3">
                                <div className="text-sm text-gray-600">{t("auth.reset.tokenMissing")}</div>
                                <Link to="/forgot-password" className="btn-outline inline-flex !py-2 text-sm">{t("auth.forgot.submit")}</Link>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
                                <p className="text-sm text-gray-500 text-center">{t("auth.reset.subtitle")}</p>
                                <div>
                                    <label className="text-xs font-mono tracking-[0.18em] text-gray-500">{t("auth.reset.new")}</label>
                                    <div className="relative mt-1">
                                        <input required type={show ? "text" : "password"} minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                                            className="w-full px-4 py-3 pr-11 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                            placeholder="••••••"
                                            data-testid="reset-password-new" />
                                        <button type="button" onClick={() => setShow((v) => !v)} className="absolute inset-y-0 right-3 grid place-items-center text-gray-400">
                                            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-mono tracking-[0.18em] text-gray-500">{t("auth.reset.confirm")}</label>
                                    <input required type={show ? "text" : "password"} minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                                        className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                        data-testid="reset-password-confirm" />
                                </div>
                                <button type="submit" disabled={loading} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="reset-password-submit">
                                    <LockKeyhole className="w-4 h-4" /> {loading ? t("auth.reset.resetting") : t("auth.reset.submit")}
                                </button>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <CheckCircle2 className="w-3 h-3 text-[var(--medx-orange)]" />
                                    {t("auth.reset.success")}
                                </div>
                            </form>
                        )}
                    </div>
                </section>
            </main>
        </div>
    );
}
